import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header/Header';
import HomePage from './pages/HomePage/HomePage';
import WhatsAppButton from './components/WhatsAppButton/WhatsAppButton';
import Loading from './components/Loading/Loading';

// Lazy-loaded routes (code splitting)
const Login = lazy(() => import('./components/Auth/Login'));
const Signup = lazy(() => import('./components/Auth/Signup'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const YachtList = lazy(() => import('./components/YachtList/YachtList'));
const ExperienceList = lazy(() => import('./components/ExperienceList/ExperienceList'));
const YachtDetail = lazy(() => import('./components/YachtDetail/YachtDetail'));
const ExperienceDetails = lazy(() => import('./components/ExperienceDetails/ExperienceDetails'));
const BookingDetails = lazy(() => import('./components/BookingDetails/BookingDetails'));
const ExperienceBookingDetails = lazy(() => import('./components/ExperienceBookingDetails/ExperienceBookingDetails'));
const MyYachts = lazy(() => import('./pages/Dashboard/MyYachts'));
const AddYacht = lazy(() => import('./pages/Dashboard/AddYacht'));
const AddExperience = lazy(() => import('./pages/Dashboard/AddExperience'));
const MyExperiences = lazy(() => import('./pages/Dashboard/MyExperiences'));
const ExperienceOptions = lazy(() => import('./pages/Dashboard/ExperienceOptions'));
const EditYacht = lazy(() => import('./pages/Dashboard/EditYacht'));
const EditExperience = lazy(() => import('./pages/Dashboard/EditExperience'));
const BookingsPage = lazy(() => import('./pages/Dashboard/BookingsPage'));
const Favorites = lazy(() => import('./pages/NormalUser/Favorites'));
const MyBookings = lazy(() => import('./pages/NormalUser/MyBookings'));
const YachtPricing = lazy(() => import('./pages/Dashboard/YachtPricing'));
const YachtUnavailability = lazy(() => import('./pages/Dashboard/YachtUnavailability'));
const BookingConfirmation = lazy(() => import('./components/BookingConfirmation/BookingConfirmation'));
const ExperienceBookingConfirmation = lazy(() => import('./components/ExperienceBookingConfirmation/ExperienceBookingConfirmation'));
const Profile = lazy(() => import('./pages/NormalUser/Profile'));
const ContactPage = lazy(() => import('./pages/Contact/ContactPage'));
const TermsOfService = lazy(() => import('./pages/Terms/TermsOfService'));
const HoursOfDay = lazy(() => import('./pages/Guide/HoursOfDay'));
const PierGuides = lazy(() => import('./pages/Guide/PierGuides'));
const YachtAddOns = lazy(() => import('./pages/Dashboard/YachtAddOns'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const ExperienceBookingsPage = lazy(() => import('./pages/Dashboard/ExperienceBookingsPage'));
const BlogList = lazy(() => import('./pages/Blog/BlogList'));
const BlogDetail = lazy(() => import('./pages/Blog/BlogDetail'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#2C7D89',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const MainContent = () => {
  const location = useLocation();
  const isYachtListPage = location.pathname === '/explore-yachts';

  return (
    <Box
      component="main"
      sx={{
        paddingTop: isYachtListPage ? '10px' : '0px', // Adjust based on header height
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><Loading width="100px" height="100px" /></Box>}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/explore-yachts" element={<YachtList />} />
          <Route path="/experiences" element={<ExperienceList />} />
          <Route path="/experience/:id" element={<ExperienceDetails />} />
          <Route path="/yacht/:id" element={<YachtDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/booking-details" element={<BookingDetails />} />
          <Route path="/experience-booking" element={<ExperienceBookingDetails />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-yachts" element={<MyYachts />} />
          <Route path="/add-yacht" element={<AddYacht />} />
          <Route path="/edit-yacht/:id" element={<EditYacht />} />
          <Route path="/yacht-bookings" element={<BookingsPage />} />
          <Route path="/my-bookings" element={<MyBookings />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/yacht-pricing" element={<YachtPricing />} />
          <Route path="/yacht-add-ons" element={<YachtAddOns />} />
          <Route path="/add-experience" element={<AddExperience />} />
          <Route path="/my-experiences" element={<MyExperiences />} />
          <Route path="/experience-options" element={<ExperienceOptions />} />
          <Route path="/edit-experience/:id" element={<EditExperience />} />
          <Route path="/experience-bookings" element={<ExperienceBookingsPage />} />
          <Route path="/manage-unavailability" element={<YachtUnavailability />} />
          <Route path="/booking-confirmation/:orderId" element={<BookingConfirmation />} />
          <Route path="/booking-confirmation" element={<BookingConfirmation />} />
          <Route path="/experience-booking-confirmation/:orderId" element={<ExperienceBookingConfirmation />} />
          <Route path="/experience-booking-confirmation" element={<ExperienceBookingConfirmation />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/hours-of-the-day" element={<HoursOfDay />} />
          <Route path="/pier-guides" element={<PierGuides />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
        </Routes>
      </Suspense>
    </Box>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Header />
              <MainContent />
              <WhatsAppButton />
            </Box>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
